// Log levels for the application
export const LogLevel = {
  ERROR: 'error',
  WARN: 'warn',
  INFO: 'info',
  DEBUG: 'debug',
} as const;

export type LogLevel = (typeof LogLevel)[keyof typeof LogLevel];

// Browser-compatible log formatter
const formatLogMessage = (level: string, message: string, meta?: any, error?: Error): string => {
  const timestamp = new Date().toISOString().replace('T', ' ').substring(0, 19);
  let logEntry = `[${timestamp}] ${level.toUpperCase()}: ${message}`;

  if (error?.stack) {
    logEntry += `\nStack: ${error.stack}`;
  }

  if (meta && Object.keys(meta).length > 0) {
    logEntry += `\nMeta: ${JSON.stringify(meta, null, 2)}`;
  }

  return logEntry;
};

// Browser console logger with colors
const logToConsole = (level: string, message: string, meta?: any, error?: Error) => {
  const isDev = import.meta.env.VITE_ENABLE_DEBUG === 'true';
  const formattedMessage = formatLogMessage(level, message, meta, error);

  // Only show debug logs in development
  if (level === 'debug' && !isDev) return;

  switch (level) {
    case 'error':
      console.error(formattedMessage, error || '');
      break;
    case 'warn':
      console.warn(formattedMessage);
      break;
    default:
      break;
  }
};

const isErrorLike = (
  value: unknown
): value is { message?: string; stack?: string; name?: string } => {
  return Boolean(
    value &&
      typeof value === 'object' &&
      ('message' in (value as Record<string, unknown>) ||
        'stack' in (value as Record<string, unknown>) ||
        'name' in (value as Record<string, unknown>))
  );
};

const looksLikeMetaPayload = (value: unknown): boolean => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return (
    'error' in candidate ||
    'meta' in candidate ||
    'userId' in candidate ||
    'category' in candidate ||
    'endpoint' in candidate ||
    'url' in candidate ||
    'status' in candidate ||
    'duration' in candidate
  );
};

// Backend transport for sending logs to server
class BackendTransport {
  private enabled: boolean;

  constructor() {
    this.enabled = import.meta.env.VITE_ENABLE_BACKEND_LOG_TRANSPORT === 'true';
  }

  async sendToBackend(level: string, message: string, meta?: any, error?: any) {
    // Only send critical logs to backend
    if (!this.enabled || !['error', 'warn'].includes(level)) return;

    try {
      const apiUrl = import.meta.env.VITE_API_URL;
      if (!apiUrl) return;

      const logInfo = {
        level,
        message,
        meta,
        error: error
          ? {
              message: error.message,
              stack: error.stack,
              name: error.name,
            }
          : undefined,
        timestamp: new Date().toISOString(),
        source: 'frontend',
        userAgent: navigator.userAgent,
        // Use API endpoint URL from meta if available (for slow API calls), otherwise use page URL
        url: meta?.url || window.location.href,
        userId: localStorage.getItem('userId') || 'anonymous',
      };

      await fetch(`${apiUrl}/logs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(logInfo),
      });
    } catch (error) {
      // Silent fail for logging errors to prevent infinite loops
      console.error('Failed to send log to backend:', error);
    }
  }
}

// Create backend transport instance
const backendTransport = new BackendTransport();

// Enhanced logging interface
export class Logger {
  private static instance: Logger;

  private constructor() {
    // Browser-based logger initialization
  }

  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  // Standard log methods
  debug(message: string, meta?: any): Logger {
    void message;
    void meta;
    return this;
  }

  info(message: string, meta?: any): Logger {
    logToConsole('info', message, meta);
    backendTransport.sendToBackend('info', message, meta);
    this.sendToAnalytics('info', message, meta);
    return this;
  }

  warn(message: string, meta?: any): Logger {
    logToConsole('warn', message, meta);
    backendTransport.sendToBackend('warn', message, meta);
    this.sendToAnalytics('warning', message, meta);
    return this;
  }

  error(message: string, error?: Error | any, meta?: any): Logger {
    let normalizedError: any = error;
    let normalizedMeta = meta;

    // Support common shorthand usage: logger.error('msg', { ...meta, error })
    if (
      meta === undefined &&
      error &&
      typeof error === 'object' &&
      !(error instanceof Error) &&
      looksLikeMetaPayload(error)
    ) {
      normalizedMeta = error;
      const nestedError = (error as Record<string, unknown>).error;
      normalizedError = isErrorLike(nestedError) ? nestedError : undefined;
    }

    const errorInfo = {
      message,
      error: normalizedError?.message || normalizedError || normalizedMeta?.error,
      stack:
        normalizedError?.stack ||
        (isErrorLike(normalizedMeta?.error) ? normalizedMeta.error.stack : undefined),
      ...normalizedMeta,
    };

    logToConsole('error', message, errorInfo, normalizedError);
    backendTransport.sendToBackend('error', message, errorInfo, normalizedError);
    this.sendToAnalytics('error', message, errorInfo);

    return this;
  }

  // Business logic specific methods
  userAction(action: string, userId?: string, details?: any) {
    this.info(`User Action: ${action}`, {
      userId: userId || localStorage.getItem('userId'),
      action,
      details,
      category: 'user_action',
    });
  }

  apiCall(method: string, url: string, status: number, responseTime: number, error?: any) {
    const logData = {
      method,
      url,
      status,
      responseTime,
      category: 'api_call',
    };

    if (status >= 400) {
      this.error(`API Error: ${method} ${url} - ${status}`, error, logData);
    } else {
      this.info(`API Call: ${method} ${url} - ${status}`, logData);
    }
  }

  performanceMetric(metric: string, value: number, unit: string = 'ms') {
    this.info(`Performance: ${metric}`, {
      metric,
      value,
      unit,
      category: 'performance',
    });
  }

  businessEvent(event: string, data: any) {
    this.info(`Business Event: ${event}`, {
      event,
      data,
      category: 'business_event',
    });
  }

  securityEvent(event: string, severity: 'low' | 'medium' | 'high', details?: any) {
    const logMethod =
      severity === 'high' ? this.error : severity === 'medium' ? this.warn : this.info;

    logMethod.call(this, `Security: ${event}`, {
      event,
      severity,
      details,
      category: 'security',
    });
  }

  private sendToAnalytics(level: string, message: string, meta?: any) {
    void level;
    void message;
    void meta;
  }

  // Method to set user context
  setUser(userId: string, email?: string, role?: string) {
    this.info('User context set', { userId, email, role });
  }

  clearUser() {
    this.info('User context cleared');
  }

  // Method to set additional context
  setContext(key: string, context: any) {
    this.debug(`Context set: ${key}`, context);
  }

  // Method to add breadcrumb
  addBreadcrumb(
    message: string,
    category: string,
    level: 'info' | 'warning' | 'error' = 'info',
    data?: any
  ) {
    void message;
    void category;
    void level;
    void data;
  }
}

// Export singleton instance
export const logger = Logger.getInstance();

// Export convenience methods
export const log = {
  debug: (message: string, meta?: any) => logger.debug(message, meta),
  info: (message: string, meta?: any) => logger.info(message, meta),
  warn: (message: string, meta?: any) => logger.warn(message, meta),
  error: (message: string, error?: Error | any, meta?: any) => logger.error(message, error, meta),
  userAction: (action: string, userId?: string, details?: any) =>
    logger.userAction(action, userId, details),
  apiCall: (method: string, url: string, status: number, responseTime: number, error?: any) =>
    logger.apiCall(method, url, status, responseTime, error),
  performanceMetric: (metric: string, value: number, unit?: string) =>
    logger.performanceMetric(metric, value, unit),
  businessEvent: (event: string, data: any) => logger.businessEvent(event, data),
  securityEvent: (event: string, severity: 'low' | 'medium' | 'high', details?: any) =>
    logger.securityEvent(event, severity, details),
  setUser: (userId: string, email?: string, role?: string) => logger.setUser(userId, email, role),
  clearUser: () => logger.clearUser(),
};
